// DataExportCore.jsx
import React, { Component, Fragment } from 'react';
import { Modal, ButtonToolbar } from 'reactstrap';
import Button from '@material-ui/core/Button';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { getFormValues } from 'redux-form';
import { setUserProfile } from '../../../redux/actions/authActions';

import { RTLProps, ThemeProps } from '../../../shared/prop-types/ReducerProps';
import buyersListActions from '../../../redux/actions/buyersListActions';
import LinearLoading from '../LinearLoading';

import { Tooltip } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';

import CloseIcon from 'mdi-react/CloseIcon';
import HelpIcon from 'mdi-react/HelpCircleOutlineIcon';
import ContactIcon from 'mdi-react/ForumOutlineIcon';
import LockIcon from 'mdi-react/LockOutlineIcon';

import SnackBar from '../SimpleSnackBar';
import TrialSubscribe from './../payment/TrialSubscribe';
import StripeCardSetupModal from './../payment/stripe/StripeCardSetupModal';

import { subscriptionPlans, dataExportCreditMap, coreTranslate, calculateCredits, translateCreditToDollars, costPerCredit, maxDownload, DataCreditUsageProgress } from './../payment/plans.js';

import {
	_capitalizeText,
	_formatNumber,
	_formatNumberDecimal
} from '../../../shared/helpers/utils';
import { _axiosCall } from '../../helpers/apicalls';

const apiUrl = process.env.API_ROOT;

// Stripe product ID for standard data credits (no price attached — amount is set per transaction)
const STRIPE_DATA_CREDITS_PRODUCT_ID =
	process.env.NODE_ENV === "development" || process.env.NODE_ENV === "local"
		? 'prod_TzX2qCGn67yDwm'
		: 'prod_TzX3sv8VRVfgHc';

// Stripe tax rate ID for NYS Sales Tax (same as subscription plans in plans.js)
const STRIPE_TAX_RATE_ID =
	process.env.NODE_ENV === "development" || process.env.NODE_ENV === "local"
		? 'txr_1HPuUwG44QC64PsRi91xssbx'
		: 'txr_1HCVNJG44QC64PsRdrtshCba';

class DataExportCore extends Component {
	static propTypes = {
		actions: PropTypes.shape({
			editMyBuyersList: PropTypes.func,
			completeMyBuyersList: PropTypes.func,
			deleteMyBuyersList: PropTypes.func,
			addMyBuyersList: PropTypes.func,
			togglePriorityFilter: PropTypes.func,
		}).isRequired,
		theme: ThemeProps.isRequired,
		t: PropTypes.func.isRequired,
		rtl: RTLProps.isRequired,
		user: PropTypes.object.isRequired,
		formValues: PropTypes.object,
		core_name: PropTypes.string.isRequired,
		q: PropTypes.string,
		fq: PropTypes.string,
		fl: PropTypes.string,
		sort: PropTypes.string,
		parentSnackOpen: PropTypes.func, // Optional prop for external snackbar
		title: PropTypes.string.isRequired,
		description: PropTypes.string,
		ExportFormComponent: PropTypes.elementType.isRequired, // The specific Redux Form component
		defaultExportName: PropTypes.string.isRequired,
		exportType: PropTypes.oneOf(['data', 'owner', 'broker']).isRequired, // 'data', 'owner', 'broker'
		renderButton: PropTypes.func.isRequired, // Function to render the trigger button
		exportCount: PropTypes.number, // Optional, for owner/broker lists
		// props for setting specific export flags on request
		isOwnerList: PropTypes.bool,
		isBrokerList: PropTypes.bool,
		requestEmail: PropTypes.bool, // For owner/broker lists if email request is an option
		setFormValues: PropTypes.func, // For Owner/Broker forms to manage state locally
		onToggle: PropTypes.func, // Optional callback for toggle
	};

	constructor(props) {
		super(props);
		this.state = {
			modal: false,
			snackShow: false,
			snackMessage: false,
			snackType: 'success',
			getCostInProgress: false,
			getInvoiceProgress: false,
			costDetails: false,
			invoiceDetails: false,
			customer: false,
			subscription: false,
			paymentMethod: false,
			doPaymentProgress: false,
			creditsNeeded: false,
			additionalCreditsNeeded: false,
			totalAmount: false,
			creditRemaining: false,
			taxes: false,
			totalAmountWithTax: false,
			userCreditUsed: false,
			creditAvail: false,
			subscribe: false,
			customSubscription: false,
			showPaymentModal: false,
			setupClientSecret: null,
			creatingCustomer: false,
			// Form values state for owner/broker lists if not using redux-form directly
			formValues: {
				exportName: props.defaultExportName,
				formatType: 'default'
			}
		};
		this.toggle = this.toggle.bind(this);
		this._requestDataExport = this._requestDataExport.bind(this);
		this.handleSubmit = this.handleSubmit.bind(this);
		this._requestDataExportCost = this._requestDataExportCost.bind(this);
		this._getSubscriptionDetails = this._getSubscriptionDetails.bind(this);
		this._doPayment = this._doPayment.bind(this);
		this._getUserCredits = this._getUserCredits.bind(this);
		this._maybeSetupStripeCustomerAndCard = this._maybeSetupStripeCustomerAndCard.bind(this);
		this._createSetupIntent = this._createSetupIntent.bind(this);
		this._checkIfCustomerHasCard = this._checkIfCustomerHasCard.bind(this);
		this._calculatePayment = this._calculatePayment.bind(this);
	}

	handleSubmit(values) {
		const { totalAmountWithTax, paymentMethod } = this.state;

		// Update local formValues state if setFormValues prop is provided
		if (this.props.setFormValues) {
			this.props.setFormValues(values);
		} else {
			// For ExportButton, which uses redux-form directly for formValues in props
			this.setState({ formValues: values });
		}

		if (totalAmountWithTax > 0) {
			if (!paymentMethod || !paymentMethod.id) {
				this._maybeSetupStripeCustomerAndCard();
			} else {
				this._doPayment();
			}
		} else {
			this._requestDataExport(false);
		}
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		// Toggle logic for modal opening/closing
		if (prevState.modal !== this.state.modal) {
			if (this.state.modal) {
				this._getSubscriptionDetails();
				this._requestDataExportCost();
				if(this.props.user && this.props.user.customerId){
					
					_axiosCall({
						url: apiUrl + '/buyerslist/stripe/get-payment-methods',
						method: 'post',
						query: {
							customer_id: this.props.user.customerId
						}
					}).then((res) => {
						if (res && res.success && res.methods && res.methods.length > 0) {
							this.setState({ paymentMethod: res.methods[0] });
						}
					}).catch((err) => {
						
					});
				}
			
			} else if (this.state.modal === false) {
				// Reset state on modal close
				this.setState({
					getCostInProgress: false,
					getInvoiceProgress: false,
					costDetails: false,
					invoiceDetails: false,
					customer: false,
					subscription: false,
					paymentMethod: false,
					doPaymentProgress: false,
					creditsNeeded: false,
					additionalCreditsNeeded: false,
					totalAmount: false,
					creditRemaining: false,
					taxes: false,
					totalAmountWithTax: false,
					userCreditUsed: false,
					planCredits: false,
					creditAvail: false,
					subscribe: false,
					customSubscription: false,
					showPaymentModal: false,
					setupClientSecret: null,
					creatingCustomer: false
				});
			}
		}

		// Calculate payment when costDetails and userCreditUsed are available
		if (this.state.creditsNeeded === false && this.state.costDetails !== false && this.state.userCreditUsed !== false) {
			this._calculatePayment(this.state.costDetails);
		}
	}

	snackOpen = (message, type = 'success') => {
		// Use parent snackbar if provided, otherwise use internal
		if (this.props.parentSnackOpen) {
			this.props.parentSnackOpen(message, type);
		} else {
			this.setState({ snackShow: true, snackMessage: message, snackType: type });
		}
	};

	snackClose = (event, reason) => {
		if (reason === 'clickaway') {
			return;
		}
		this.setState({ snackShow: false, snackMessage: '' });
	};

	_maybeSetupStripeCustomerAndCard() {
		const { customSubscription, additionalCreditsNeeded, paymentMethod } = this.state;
		const { user } = this.props;
		const { setUserProfile } = this.props.actions;

		if (!customSubscription || additionalCreditsNeeded <= 0) return;
		if (paymentMethod && paymentMethod.id) return; // Already have card on file

		const customerId = user && user.customerId ? user.customerId : null;

		if (!customerId) {
			this.setState({ creatingCustomer: true });

			_axiosCall({
				url: apiUrl + '/buyerslist/stripe/create-basic-customer',
				method: 'post',
				query: {
					userId: user.id,
					name: (user.firstName && user.lastName) ? user.firstName + " " + user.lastName : "",
					email: user.email
				}
			}).then((res) => {
				if (res && res.customerId) {
					this.props.actions.setUserProfile({ ...user, customerId: res.customerId });
					this.setState({ customer: { id: res.customerId }, creatingCustomer: false }, () => {
						this._checkIfCustomerHasCard(res.customerId);
					});
				}
			}).catch((err) => {
				console.error('Error creating customer:', err);
				this.snackOpen('Failed to set up payment. Please try again.', 'error');
				this.setState({ creatingCustomer: false });
			});

		} else {
			this.setState({ customer: { id: customerId } }, () => {
				this._checkIfCustomerHasCard(customerId);
			});
		}
	}

	_createSetupIntent() {
		const { customer } = this.state;
		if (!customer || !customer.id) return;

		_axiosCall({
			url: apiUrl + '/buyerslist/stripe/create-setup-intent',
			method: 'post',
			query: {
				customer_id: customer.id
			}
		}).then((res) => {
			if (res && res.client_secret) {
				this.setState({
					setupClientSecret: res.client_secret,
					showPaymentModal: true
				});
			} else {
				this.snackOpen('Failed to initiate payment setup. Please try again.', 'error');
			}
		}).catch((err) => {
			console.error('Failed to create SetupIntent:', err);
			this.snackOpen('Failed to initiate payment setup. Please try again.', 'error');
		});
	}

	_checkIfCustomerHasCard(customerId) {
		_axiosCall({
			url: apiUrl + '/buyerslist/stripe/get-payment-methods',
			method: 'post',
			query: {
				customer_id: customerId
			}
		}).then((res) => {
			if (res && res.success && res.methods && res.methods.length > 0) {
				this.setState({ paymentMethod: res.methods[0] }, () => {
					this._doPayment(); // 🔥 go ahead and pay
				});
			} else {
				this._createSetupIntent(); // Prompt for new card
			}
		}).catch((err) => {
			console.error('Error checking payment methods:', err);
			this.snackOpen('Error checking payment methods. Please try again.', 'error');
			this._createSetupIntent(); // fallback
		});
	}

	_calculatePayment(costDetails) {
		const { user, exportType } = this.props;
		const { subscriptionPlan, monthlyDataCreditOverride } = user;
		const { userCreditUsed } = this.state;

		let currentPlan = subscriptionPlans.filter(plan => plan.stripeProductId === subscriptionPlan)[0];
		if (!currentPlan) {
			currentPlan = subscriptionPlans.filter(plan => user.role.includes(plan.id) ||
				(plan.altIds && plan.altIds.some(altId => user.role.includes(altId))))[0];
		}

		let dataCredits = 0;
		if (currentPlan && currentPlan.dataCredits) {
			dataCredits = currentPlan.dataCredits;
		}

		if (typeof monthlyDataCreditOverride === 'number' && monthlyDataCreditOverride >= 0) {
			dataCredits = monthlyDataCreditOverride;
		}
		if (user.role.indexOf('admin') >= 0) {
			dataCredits = 9999999; // Admin has unlimited credits
		}

		let creditAvail = dataCredits - userCreditUsed;
		if (creditAvail < 0) creditAvail = 0;

		let creditsNeeded = 0;
		let additionalCreditsNeeded = 0;
		let totalAmount = 0;
		let creditRemaining = 0;
		let taxes = 0;
		let totalAmountWithTax = 0;

		if (costDetails) {
			creditsNeeded = calculateCredits(costDetails.core_name, costDetails.rows, costDetails.facets, exportType);
			additionalCreditsNeeded = creditsNeeded - creditAvail;
			if (additionalCreditsNeeded < 0) additionalCreditsNeeded = 0;

			if (additionalCreditsNeeded > 0) {
				totalAmount = translateCreditToDollars(additionalCreditsNeeded);
				taxes = totalAmount * 0.08875; // NYC sales tax
				totalAmountWithTax = (parseFloat(totalAmount) + parseFloat(taxes)).toFixed(2);
			}

			creditRemaining = creditAvail - creditsNeeded;
			if (creditRemaining < 0) creditRemaining = 0;

			this.setState({
				planCredits: dataCredits,
				creditAvail: creditAvail,
				creditsNeeded: creditsNeeded,
				additionalCreditsNeeded: additionalCreditsNeeded,
				totalAmount: totalAmount,
				creditRemaining: creditRemaining,
				taxes: taxes,
				totalAmountWithTax: totalAmountWithTax
			});
		}
	}

	_requestDataExportCost() {
		if (this.state.getCostInProgress) return;
		this.setState({ getCostInProgress: true });
		
		const costFacets = {
			"we3-properties": {
				"by_status": {
					"type": "terms",
					"field": "current_status"
				}
			},
			"we3-buildings": {
				"by_units": {
					"range": {
						"field": "inventory_total",
						"start": 0,
						"end": 1000,
						"gap": "+50"
					}
				}
			},
			// Add any other core_name specific facets here if needed
		};

		const { user, core_name, q, fq, fl, sort, exportCount, exportType } = this.props;
		// Use local formValues state if available, otherwise fallback to prop formValues (for ExportButton)
		const currentFormValues = this.props.formValues || this.state.formValues;

		let userData = {
			url: apiUrl + '/dataexport/get-cost',
			method: "post",
			query: {
				user: user,
				core_name: core_name,
				export_name: currentFormValues && currentFormValues.exportName,
				options: {
					q: q,
					fq: fq,
					fl: fl,
					sort: sort,
					// Limit rows based on exportCount prop if available and within maxDownload
					rows: (exportCount !== undefined && exportCount < maxDownload(core_name, user)) ? exportCount : null
				}
			}
		};

		if (costFacets[core_name] && exportType != "owner") {
			userData.query.options["json.facet"] = costFacets[core_name];
		}

		_axiosCall(userData)
			.then(res => {
				console.log("_requestDataExportCost res back ,", res);
				this.setState({ getCostInProgress: false });
				if (res && res.success) {
					this.setState({ costDetails: res });
				} else {
					this.setState({ costDetails: null });
				}
			})
			.catch((error) => {
				console.error("_requestDataExportCost error ", error);
				this.snackOpen('Failed to get export cost. Please try again.', 'error');
				this.setState({ costDetails: null, getCostInProgress: false });
			});
	}

	_getSubscriptionDetails() {
		const { user } = this.props;
		const { subscriptionId } = user;

		if (user.role.indexOf('admin') >= 0) {
			this.setState({
				customer: user,
				subscription: {},
				paymentMethod: {},
				userCreditUsed: 0
			});
			return;
		}

		// User without a subscription - prepaid or custom plan (handles 'manual' downloadAccess and SSO)
		// Check SSO/manual BEFORE bailing on missing subscriptionId
		if (user.downloadAccess === 'manual' || (user.ssoOrganiztion && user.ssoOrganiztion === "cbwarburg")) {
			var date = new Date(), y = date.getFullYear(), m = date.getMonth();
			var dayOfMonth = user.dataDownloadResetDayOfMonth ? user.dataDownloadResetDayOfMonth : 1;
			var firstDay = Math.floor(new Date(y, m, dayOfMonth).getTime() / 1000);
			var lastDay = Math.floor(new Date(y, m + 1, dayOfMonth - 1).getTime() / 1000);
			const subscription = {
				current_period_start: firstDay,
				current_period_end: lastDay
			};
			this._getUserCredits(subscription);
			this.setState({
				customer: user && user.customerId ? {id: user.customerId} : {},
				subscription: {},
				paymentMethod: {},
				customSubscription: true
			});
			return;
		}

		if (!subscriptionId) return false;
			
		

		let userData = {
			url: apiUrl + '/buyerslist/stripe/get-subscription',
			method: "post",
			query: {
				subscription_id: subscriptionId
			}
		};

		_axiosCall(userData)
			.then(res => {
				console.log("_getSubscriptionDetails res back ,", res);
				if (res && res.customer) {
					this._getUserCredits(res.subscription);
					this.setState({
						customer: res.customer,
						subscription: res.subscription,
						paymentMethod: res.paymentMethod
					});
				} else {
					this.setState({
						customer: user,
						subscription: {},
						paymentMethod: {}
					});
				}
			})
			.catch((error) => {
				console.error("_getSubscriptionDetails error ", error);
				this.snackOpen('Failed to get subscription details. Please try again.', 'error');
				this.setState({
					customer: user,
					subscription: {},
					paymentMethod: {}
				});
			});
	}

	_getUserCredits(subscription) {
		function getAdjustedPeriodRange(currentPeriodStart, currentPeriodEnd) {
			const now = new Date();
			const oneMonthAgo = new Date(now);
			oneMonthAgo.setMonth(now.getMonth() - 1);

			const startDate = new Date(currentPeriodStart * 1000);
			const endDate = new Date(currentPeriodEnd * 1000);

			if (startDate < oneMonthAgo) {
				// Adjust to same day-of-month in current month
				const adjustedStartDate = new Date(
					now.getFullYear(),
					now.getMonth(),
					startDate.getDate(),
					startDate.getHours(),
					startDate.getMinutes(),
					startDate.getSeconds()
				);

				const adjustedEndDate = new Date(adjustedStartDate);
				adjustedEndDate.setMonth(adjustedEndDate.getMonth() + 1); // 1 month after adjustedStart

				return {
					adjustedStart: Math.floor(adjustedStartDate.getTime() / 1000),
					adjustedEnd: Math.floor(adjustedEndDate.getTime() / 1000)
				};
			}

			// No adjustment needed
			return {
				adjustedStart: currentPeriodStart,
				adjustedEnd: currentPeriodEnd
			};
		}

		const creditDateRange = getAdjustedPeriodRange(subscription.current_period_start, subscription.current_period_end);
		const { id } = this.props.user;

		let userData = {
			url: apiUrl + '/dataexport/analytics/aggregate',
			method: "post",
			query: [
				{
					"$match": {
						"$and": [
							{ "user_key": id },
							{
								"created_timestamp_unix": {
									"$gte": creditDateRange.adjustedStart,
									"$lt": creditDateRange.adjustedEnd
								}
							}
						]
					}
				},
				{
					"$group": {
						"_id": null,
						"total": {
							"$sum": "$credits"
						}
					}
				}
			]
		};

		_axiosCall(userData)
			.then(res => {
				console.log("_getUserCredits res back ,", res);
				if (res && res.length > 0 && res[0].total) {
					this.setState({ userCreditUsed: res[0].total });
				} else {
					this.setState({ userCreditUsed: 0 });
				}
			})
			.catch((error) => {
				console.error("_getUserCredits error ", error);
				this.snackOpen('Failed to get user credit usage. Please try again.', 'error');
				this.setState({ userCreditUsed: 0 });
			});
	}

	_doPayment() {
		if (this.state.doPaymentProgress) return;
		this.setState({ doPaymentProgress: true });

		const { customer, paymentMethod, additionalCreditsNeeded, totalAmountWithTax, taxes, totalAmount } = this.state;
		const { user } = this.props;
		const description = `Purchase of ${_formatNumber(additionalCreditsNeeded)} Marketproof Data Credits ($${costPerCredit} per credit) + NYC Sales Tax`;
		const stripeAmount = parseInt(parseFloat(totalAmountWithTax) * 100);

		const creditConfig = {
			credits: additionalCreditsNeeded,
			pricePerCredit: costPerCredit,
			subtotal: totalAmount,
			tax: taxes,
			total: parseFloat(totalAmountWithTax),
			stripeProductId: STRIPE_DATA_CREDITS_PRODUCT_ID,
			taxCode: STRIPE_TAX_RATE_ID
		};

		let userData = {
			url: apiUrl + '/buyerslist/stripe/onetime',
			method: "post",
			query: {
				customer: customer.id,
				amount: stripeAmount,
				payment_method: paymentMethod.id,
				description: description,
				payment_type: 'data_credits',
				user_id: user.id,
				user_email: user.email,
				creditConfig: creditConfig
			}
		};

		_axiosCall(userData)
			.then(res => {
				console.log("_doPayment res back ,", res);
				this.setState({ doPaymentProgress: false });
				var payment = res && res.payment ? res.payment : res;
				if (payment && (payment.status === 'succeeded' || payment.amount_received === stripeAmount)) {
					this._requestDataExport(payment);
				} else {
					this.snackOpen('Oops, something went wrong, your payment was not successful. Please try again.', 'error');
				}
			})
			.catch((error) => {
				console.error("_doPayment error ", error);
				this.snackOpen('Oops, something went wrong with your payment. Please try again.', 'error');
				this.setState({ doPaymentProgress: false });
			});
	}

	_requestDataExport(payment) {
		const { user, core_name, q, fq, fl, sort, exportCount, exportType, requestEmail } = this.props;
		// Use local formValues state if available, otherwise fallback to prop formValues (for ExportButton)
		const currentFormValues = this.props.formValues || this.state.formValues;

		const { creditsNeeded, additionalCreditsNeeded, taxes, totalAmountWithTax } = this.state;

		let userData = {
			url: apiUrl + '/dataexport',
			method: "post",
			query: {
				user: user,
				core_name: core_name,
				export_name: currentFormValues && currentFormValues.exportName,
				export_format: currentFormValues && currentFormValues.formatType,
				credits: creditsNeeded,
				credits_paid: additionalCreditsNeeded,
				payment_id: payment ? payment.id : 'credit',
				payment_taxes: parseFloat(taxes),
				payment_amount: parseFloat(totalAmountWithTax),
				options: {
					q: q,
					fq: fq,
					fl: fl,
					sort: sort,
					rows: (exportCount !== undefined && exportCount < maxDownload(core_name, user)) ? exportCount : maxDownload(core_name, user)
				}
			}
		};

		// Add specific flags based on exportType
		if (exportType === 'owner') {
			userData.query.is_owner_list = true;
			userData.query.request_owner_email = requestEmail || false;
		} else if (exportType === 'broker') {
			userData.query.is_broker_list = true;
			// Brokers generally don't have email request checkbox, but keeping it generic
			userData.query.request_owner_email = requestEmail || false;
		}

		_axiosCall(userData)
			.then(res => {
				console.log("_requestDataExport res back ,", res);
				if (res && res.success) {
					this.snackOpen(<Fragment><span>Your data export request was successful! <p className="small-text light-text" style={{ marginLeft: '30px', color: '#FFF' }}>You will receive an email when your data export is ready.</p></span></Fragment>);
					this.setState({ modal: false });
				} else {
					if (res && res.message) {
						this.snackOpen(<Fragment><span>Oops, your data export was not successful. <p className="small-text light-text" style={{ marginLeft: '30px', color: '#FFF' }}>{_capitalizeText(res.message)}</p></span></Fragment>, 'error');
					} else {
						this.snackOpen('Oops, something went wrong, your data export was not successful.', 'error');
					}
				}
			})
			.catch((error) => {
				console.error("_requestDataExport error ", error);
				this.snackOpen('Oops, something went wrong, your data export was not successful.', 'error');
			});
	}

	toggle(event) {
		if (event && typeof event.stopPropagation === 'function') {
			event.stopPropagation();
		}
		const { onToggle } = this.props;
		if (onToggle && typeof onToggle === 'function') {
			onToggle(event);
		}
		this.setState(prevState => ({ modal: !prevState.modal }));
	}

	modalClass = classNames({
		'modal-dialog--colored': false,
		'modal-dialog--header': true,
	});

	setSubscribe = (subscribe) => {
		this.setState({ subscribe: subscribe });
	};

	render() {
		const { user, title, description, core_name, ExportFormComponent, renderButton, exportCount, fetchInProgress, customClassName, menuButton } = this.props;
		const {
			modal, snackShow, snackMessage, snackType, costDetails,
			customer, subscription, paymentMethod, planCredits, creditAvail,
			creditsNeeded, additionalCreditsNeeded, totalAmount, creditRemaining,
			taxes, totalAmountWithTax, doPaymentProgress, userCreditUsed, subscribe,
			customSubscription, showPaymentModal, setupClientSecret, creatingCustomer
		} = this.state;

		if (!user || !user.subscriptionPlan || (user.downloadAccess && user.downloadAccess === "disabled")) {
			return null;
		}

		if (user.subscriptionStatus === 'trialing') {
			return (
				<Fragment>
					{renderButton({ onClick: () => this.setSubscribe(true) })}
					<TrialSubscribe subscribe={subscribe} setSubscribe={this.setSubscribe} dataDownload />
				</Fragment>
			);
		}

		const currentFormValues = this.props.formValues || this.state.formValues;

		const recordsText = costDetails ? `${_formatNumber(costDetails.rows)} ${coreTranslate[costDetails.core_name]}` : '0 records';
		const recordPlural = costDetails && costDetails.rows > 1 ? 's' : '';
		const shadowUnitsText = (costDetails && coreTranslate[costDetails.core_name] === "unit" && costDetails.facets && costDetails.facets.by_status && costDetails.facets.by_status.buckets && costDetails.facets.by_status.buckets.filter(bucket => bucket.val === "shadow" && bucket.count > 0).length > 0) ?
			` including ${_formatNumber(costDetails.facets.by_status.buckets.filter(bucket => bucket.val === "shadow" && bucket.count > 0)[0].count)} shadow units` : '';

		const maxDownloadLimit = maxDownload(core_name, user);
		const downloadLimitExceeded = costDetails && costDetails.rows > maxDownloadLimit;
		const exportCountLimitExceeded = exportCount !== undefined && exportCount > maxDownloadLimit && !(customSubscription && additionalCreditsNeeded > 0);
		const noRecordsAvailable = costDetails && costDetails.rows < 1;
console.log(this.state,user)
		return (
			<Fragment>
				{showPaymentModal &&
					<StripeCardSetupModal
						isOpen={showPaymentModal}
						clientSecret={setupClientSecret}
						user={user}
						onClose={() => this.setState({ showPaymentModal: false })}
						onComplete={() => {
							this.setState({ showPaymentModal: false }, () => {
								const customerId = customer && customer.id;
								if (!customerId) return;
								_axiosCall({
									url: apiUrl + '/buyerslist/stripe/get-payment-methods',
									method: 'post',
									query: { customer_id: customerId }
								}).then((res) => {
									if (res && res.success && res.methods && res.methods.length > 0) {
										this.setState({ paymentMethod: res.methods[0] }, () => {
											this._doPayment(); // 🔥 immediately pay after card is added
										});
									} else {
										console.error('No payment method found after setup.');
										this.snackOpen('Failed to retrieve payment method after setup.', 'error');
									}
								}).catch((err) => {
									console.error('Failed to retrieve payment method after setup:', err);
									this.snackOpen('Failed to retrieve payment method after setup.', 'error');
								});
							});
						}}
					/>
				}
				{!this.props.parentSnackOpen && (
					<SnackBar
						snackShow={snackShow}
						snackMessage={snackMessage}
						snackClose={this.snackClose}
						snackDuration={5000}
						snackType={snackType}
					/>
				)}

				{renderButton({ onClick: this.toggle })}

				<Modal
					isOpen={modal}
					toggle={this.toggle}
					modalClassName={`ltr-support`}
					className={`modal-dialog--primary ${this.modalClass} ${user && user.role && Array.isArray(user.role) ? user.role.join(" ") : ""}`}
				>
					<div className="modal__header">
						<button className="lnr lnr-cross modal__close-btn" type="button" onClick={this.toggle} />
						<h4 className="text-modal modal__title">{title}</h4>
					</div>

					<div className="modal__body background_white">
						{(costDetails === false || customer === false || creditsNeeded === false || creatingCustomer) ?
							<div className="text-center">
								<LinearLoading />
								<h4>{creatingCustomer ? 'Setting up payment...' : 'Calculating data credits...'}</h4>
							</div>
							:
							<Fragment>
								{noRecordsAvailable ? (
									<div>
										{description && <p style={{ fontSize: 15 }}>{description}</p>}
										<div className="modal__message-container"  style={{ marginTop: 20 }}>
											<h4 className={"semi-bold-text mb-2"}>No {coreTranslate[core_name]} available</h4>
											Export contains <span className="semi-bold-text">no {coreTranslate[core_name]} records</span>.<br />
											<div className="text-right mt-3">
												<a href="https://help.marketproof.com/en/articles/4857038-guide-to-exporting-data-on-marketproof-new-development" className="link-text small-text" target="_blank" rel="noopener noreferrer">Learn More <HelpIcon size={12} style={{ marginTop: -3 }} /></a>
											</div>
										</div>
										<hr />
										<ButtonToolbar className="modal__footer" style={{ padding: 0 }}>
											<Tooltip title="Cancel">
												<Button
													onClick={this.toggle}
													variant="outlined"
													color=""
													className=""
													startIcon={null}
													style={{ marginRight: 10 }}
												> Cancel
												</Button>
											</Tooltip>{' '}
										</ButtonToolbar>
									</div>
								) : downloadLimitExceeded ? (
									<div>
										{description && <p style={{ fontSize: 15 }}>{description}</p>}
										<div className="modal__message-container" style={{marginTop: 20 }}>
											<h4 className={"semi-bold-text mb-2"}>Download Limit Exceeded</h4>
											Export contains <span className="semi-bold-text">{_formatNumber(costDetails.rows)}</span> records which exceeds the <span className="semi-bold-text">{_formatNumber(maxDownloadLimit)}</span> maximum {coreTranslate[costDetails.core_name]} records allowed for each download.<br /><br />
											Please narrow your search to reduce the total number of records in your dataset.
											<div className="text-right mt-3">
												<a href="https://help.marketproof.com/en/articles/4857038-guide-to-exporting-data-on-marketproof-new-development" className="link-text small-text" target="_blank" rel="noopener noreferrer">Learn More <HelpIcon size={12} style={{ marginTop: -3 }} /></a>
											</div>
										</div>
										<hr />
										<ButtonToolbar className="modal__footer" style={{ padding: 0 }}>
											<Tooltip title="Cancel">
												<Button
													onClick={this.toggle}
													variant="outlined"
													color=""
													className=""
													startIcon={null}
													style={{ marginRight: 10 }}
												> Cancel
												</Button>
											</Tooltip>{' '}
											<Tooltip title="Contact Us">
												<Button
													variant="contained"
													color="primary"
													className="buyersList__primary-button intercom_launch"
													startIcon={<ContactIcon />}
												> Contact Us
												</Button>
											</Tooltip>
										</ButtonToolbar>
									</div>
								) : (
									<div>
										{exportCountLimitExceeded && (
											<div className="modal__message-container" style={{  marginTop: 10, marginBottom: 25 }}>
												Your search contains <span className="semi-bold-text">{_formatNumber(exportCount)}</span> {coreTranslate[core_name]}s, exceeding the maximum limit. Only the first <span className="semi-bold-text">{_formatNumber(maxDownloadLimit)}</span> will be exported. To ensure all {coreTranslate[core_name]}s are included, consider narrowing your search.
											</div>
										)}
										{additionalCreditsNeeded > 0 ? 
										<div className="modal__message-container elliman-only" style={{ display:"none",  marginTop: 10, marginBottom: 25, fontSize:16 }}>
											<h4 style={{fontWeight:600,marginBottom:10}}>Additional Credits Needed</h4>
											{paymentMethod && paymentMethod.card ? 
											<div>This purchase exceeds the 25 monthly credits provided by Douglas Elliman. Click “Pay & Export” to charge your card on file for the extra credits and complete your purchase.</div>
											:
											<div>This purchase exceeds the 25 monthly credits provided by Douglas Elliman. To continue, click “Pay & Export” to add your credit card and purchase extra credits directly.</div>
											}
										</div> : null}
										<p style={{ fontSize: 15 }}>
											{description ? description : 'Please confirm your data download request.'}<br /> <br />
											This request will export <span className="semi-bold-text">{recordsText}</span> record{recordPlural}
											{shadowUnitsText}.
										</p>
										<hr />
										<div className={"mb-2"}>
											<h4 className="semi-bold-text" style={{ fontSize: 16, display: "inline-block" }}>Confirm Data Credits</h4>
											<a href="https://help.marketproof.com/en/articles/4857038-guide-to-exporting-data-on-marketproof-new-development" className="link-text float-right small-text" target="_blank" rel="noopener noreferrer">Learn More <HelpIcon size={12} style={{ marginTop: -3 }} /></a>
										</div>
										<p style={{ fontSize: 15 }} className={"mb-3"}>
											<DataCreditUsageProgress credit_total={planCredits} credit_used={userCreditUsed} />
										</p>

										{false && customSubscription && additionalCreditsNeeded > 0 ? (
											<div className="data-credit-details">
												<div className="charge-amount">
													<div  className="modal__message-container"  style={{ marginTop: 20 }}>
														<h4 className={"semi-bold-text mb-2"}>Data Credits Exceeded</h4>
														This export requires <span className="semi-bold-text">{_formatNumber(creditsNeeded)} credits</span> which exceed your available Data Credits. Please reduce the number of records in your search or contact us to increase your Data Credit limit.
													</div>
												</div>
												<ButtonToolbar className="modal__footer" style={{ padding: 0 }}>
													<Tooltip title="Cancel">
														<Button
															onClick={this.toggle}
															variant="outlined"
															color=""
															className=""
															startIcon={null}
															style={{ marginRight: 10 }}
														> Cancel
														</Button>
													</Tooltip>{' '}
													<Tooltip title="Contact Us">
														<Button
															variant="contained"
															color="primary"
															className="buyersList__primary-button intercom_launch"
															startIcon={<ContactIcon />}
														> Contact Us
														</Button>
													</Tooltip>
												</ButtonToolbar>
											</div>
										) : (
											<Fragment>
												<div style={{ display: "flex" }}>
													<div className="pricing" style={{ padding: '22px 10px', border: "1px solid #A3ADC2", borderRadius: "10px", textAlign: "center", marginRight: 20, height: 120 }}>
														<span className="small-text light-text">Needed for Export</span>
														<h3 className="pricing-plan" style={{ color: '#780F9E' }}>{_formatNumber(creditsNeeded)}</h3>
														<p className="pricing-plan mt-0">DATA CREDITS</p>
													</div>
													<div className="data-credit-details">
														<div className="charge-amount">
															<div className="checkout-line">Current Credit Balance <strong>{creditAvail} Credits</strong></div>
															{additionalCreditsNeeded > 0 ? (
																<div className="checkout-line">Additional Credits Needed <strong>{additionalCreditsNeeded ? _formatNumber(additionalCreditsNeeded) : '0'} Credits</strong></div>
															) : (
																<div className="checkout-line">Credits Needed <strong>-{creditsNeeded ? _formatNumber(creditsNeeded) : '0'} Credits</strong></div>
															)}

															{creditRemaining > 0 ? (
																<div className="checkout-line">Credit Balance After Export <strong>{creditRemaining} Credits</strong></div>
															) : (
																<div className="checkout-line">Purchase Credits <span className="small-text light-text">({_formatNumber(additionalCreditsNeeded)} x ${costPerCredit} per credit)</span> <strong>${totalAmount ? _formatNumber(totalAmount) : '0'}</strong></div>
															)}
															{taxes > 0 && (
																<div className="checkout-line">NYC Sales Tax <strong>${parseFloat(taxes).toFixed(2)}</strong></div>
															)}
															<div className="total-payment">Total Amount Due
																{additionalCreditsNeeded > 0 && <span className="small-text light-text"></span>}
																<strong>${totalAmountWithTax ? _formatNumberDecimal(totalAmountWithTax) : '0'}</strong></div>
														</div>
														{totalAmount > 0 && <span className="charge-text">
															Total amount will be charged to your credit card on file.
														</span>}
													</div>
												</div>
												<hr />
												<ExportFormComponent
													user={user}
													toggle={this.toggle}
													onSubmit={this.handleSubmit}
													export_name={currentFormValues.exportName}
													formValues={currentFormValues} // Pass current form values
													setFormValues={this.props.setFormValues || ((values) => this.setState({ formValues: values }))} // Pass updater
													btnText={totalAmountWithTax > 0 ? "Pay & Export" : "Export"}
												/>
											</Fragment>
										)}

										<div className={"modal-overlay"} style={{ display: doPaymentProgress ? 'block' : 'none' }}>
											<LinearLoading />
											<h4>Payment In Progress</h4>
											<LockIcon size={48} color={'#9600BF'} />
										</div>
									</div>
								)}
							</Fragment>
						}
					</div>
				</Modal>
			</Fragment>
		);
	}
}

function mapStateToProps(state) {
	// The formValues will be specific to each wrapper component's redux-form name
	return {
		theme: state.theme,
		rtl: state.rtl,
		user: state.user,
		// No formValues here, it will be passed down by the specific button components
	};
}

function mapDispatchToProps(dispatch) {
	return {
		actions: {
			...bindActionCreators(buyersListActions, dispatch),
			setUserProfile: bindActionCreators(setUserProfile, dispatch),
		}
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation('common')(DataExportCore));
